import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMotion, SPRING, RollingNumber } from "../motion";
import {
  Star, ChevronLeft, Plus, Minus, Copy, Check,
  Wifi, BatteryFull, SignalHigh, Loader2, CreditCard, Landmark,
  Banknote, Smartphone, Mail, MessageCircle, Hand, UtensilsCrossed, Wallet, ChevronUp,
} from "lucide-react";
import Logo from "./Logo";
import { STR } from "../strings";
import { money, fmtTime, METHOD_LABELS, post, getTableFromUrl, orderNo } from "../api";

const IMG_HERO = "https://images.unsplash.com/photo-1728033647942-ce3c2185ba70?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200&fm=webp";
const CATS = ["entradas", "principales", "pastas", "postres", "bebidas", "vinos"];
const STEPS = ["nuevo", "en_cocina", "listo", "entregado"];

const tagStyle = (t) => {
  if (t === "vegano") return "bg-[#DCFCE7] text-[#16A34A]";
  if (t === "picante") return "bg-[#FEE2E2] text-[#DC2626]";
  return "bg-[#E3F0F9] text-[#0A0A0A]";
};

// agrupa ítems repetidos: "2× Copa de Malbec" en vez de dos líneas
export function groupItems(items) {
  const map = new Map();
  for (const it of items) {
    const key = `${it.dish_id}|${it.note || ""}|${it.by || ""}`;
    if (map.has(key)) {
      const g = map.get(key);
      g.qty += it.qty;
    } else {
      map.set(key, { ...it });
    }
  }
  return [...map.values()];
}

export default function ClienteView({ state, refresh, setState, onClienteEvent, resetSignal, onGuide, isMobile, openCalc, forceScreen }) {
  const table = getTableFromUrl();
  const m = useMotion();
  const [lang, setLang] = useState("es");
  const screenDir = useRef(1);
  const setScreenDir = (next, dir = 1) => { screenDir.current = dir; setScreen(next); };
  const [addedFlash, setAddedFlash] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [stepBump, setStepBump] = useState(false);
  const t = STR[lang];
  const [myName, setMyName] = useState(localStorage.getItem("bodegon_demo_name") || "");
  const config = state?.config || {};
  const [screen, setScreen] = useState("welcome"); // welcome | menu | tracking | bill | pay | cash | receipt | review | done
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cat, setCat] = useState("entradas");
  const [noteFor, setNoteFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [tipMode, setTipMode] = useState("10");
  const [customTip, setCustomTip] = useState("");
  const [splitMode, setSplitMode] = useState(null);
  const [people, setPeople] = useState(2);
  const [pickedItems, setPickedItems] = useState([]);
  const [method, setMethod] = useState(null);
  const [mpSource, setMpSource] = useState("saldo");
  const [payPhase, setPayPhase] = useState("form"); // form | processing | done
  const [receipt, setReceipt] = useState(null);
  const [stars, setStars] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewWa, setReviewWa] = useState("");
  const [reviewSent, setReviewSent] = useState(false);
  const [cashDone, setCashDone] = useState(false);
  const [googleSim, setGoogleSim] = useState(false);
  const [copied, setCopied] = useState("");
  const [needsApproval, setNeedsApproval] = useState(false);
  const [payMode, setPayMode] = useState(null); // null | "instant" (pagar al instante)
  const [toast, setToast] = useState("");
  const [card, setCard] = useState({ number: "4509 9535 6623 3704", name: "MARIA DEMO", expiry: "11/28", cvv: "123", cuotas: "1" });
  const [cashAlertId, setCashAlertId] = useState(null);
  const pendingReceiptRef = useRef(null);

  const dishes = state?.dishes || [];
  const myOrders = useMemo(
    () => (state?.orders || []).filter((o) => o.table === table && !o.paid),
    [state, table]
  );
  const allItems = useMemo(() => myOrders.flatMap((o) => o.items), [myOrders]);
  const openTotal = allItems.reduce((s, i) => s + i.price * i.qty, 0);
  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);
  // seguimiento: también los pedidos ya pagados al instante que la cocina todavía no entregó
  const trackOrders = useMemo(
    () => (state?.orders || []).filter((o) => o.table === table && (!o.paid || (o.status !== "entregado" && o.payment_method !== "otro"))),
    [state, table]
  );
  const latestOrder = trackOrders.length ? trackOrders[trackOrders.length - 1] : null;
  const stepIndex = latestOrder ? STEPS.indexOf(latestOrder.status) : -1;
  const cashPending = (state?.alerts || []).some((a) => a.type === "efectivo" && a.table === table && a.status === "pendiente");

  // vibración corta al avanzar el estado del pedido
  const prevStepRef = useRef(-1);
  useEffect(() => {
    if (stepIndex > prevStepRef.current && prevStepRef.current >= 0 && navigator.vibrate) {
      navigator.vibrate(30);
    }
    prevStepRef.current = stepIndex;
  }, [stepIndex]);

  // total a pagar según división
  const shareBase = () => {
    if (splitMode === "equal") return Math.round(openTotal / people);
    if (splitMode === "items") return pickedItems.reduce((s, idx) => s + (allItems[idx] ? allItems[idx].price * allItems[idx].qty : 0), 0);
    return openTotal;
  };
  const payBase = shareBase();
  const tipValue = () => {
    if (tipMode === "0") return 0;
    if (tipMode === "10") return Math.round(payBase * 0.10);
    if (tipMode === "15") return Math.round(payBase * 0.15);
    return parseInt(customTip || "0", 10) || 0;
  };
  const grandTotal = payBase + tipValue();

  // cobro en efectivo confirmado por caja: se detecta cuando la alerta de ESTA mesa deja de estar pendiente
  // (sirve también para pagos parciales, donde la mesa no queda "pagada"). El comprobante se arma con
  // lo que se eligió pagar al momento de pedir el cobro, porque después la cuenta ya no está abierta.
  const pendingCashAlert = (state?.alerts || []).find((a) => a.type === "efectivo" && a.table === table && a.status === "pendiente");
  useEffect(() => {
    if (screen !== "cash" || cashDone) return;
    if (!cashAlertId) {
      if (pendingCashAlert) {
        setCashAlertId(pendingCashAlert.id);
        if (!pendingReceiptRef.current) {
          pendingReceiptRef.current = {
            id: pendingCashAlert.id, method: "efectivo", tip: pendingCashAlert.tip || 0, tip_destination: pendingCashAlert.tip_destination,
            total: (pendingCashAlert.amount || 0) + (pendingCashAlert.tip || 0), items: groupItems(allItems),
          };
        }
      }
      return;
    }
    const a = (state?.alerts || []).find((x) => x.id === cashAlertId);
    if (a && a.status !== "pendiente") {
      setCashDone(true);
      setTimeout(() => {
        setReceipt(pendingReceiptRef.current);
        setScreen("receipt");
      }, 1800);
    }
  }, [state, screen, cashAlertId, cashDone, pendingCashAlert, allItems]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2200); };

  // modo presentación: forzar la pantalla del cliente
  useEffect(() => {
    if (forceScreen) {
      setScreen(forceScreen);
      if (forceScreen === "receipt") {
        setReceipt((r) => r || { method: "efectivo", total: openTotal + 4000, tip: 4000, tip_destination: config.tip_destination || "mozo", items: groupItems(allItems) });
      }
    }
  }, [forceScreen]);

  // volver a bienvenida solo con "reiniciar demo"
  // volver a bienvenida solo con "reiniciar demo"
  const firstResetRef = useRef(true);
  useEffect(() => {
    if (firstResetRef.current) { firstResetRef.current = false; return; }
    setScreen("welcome"); setCart([]); setCartOpen(false); setStars(0); setReviewText(""); setReviewWa("");
    setReviewSent(false); setCashDone(false); setMethod(null); setReceipt(null); setPayPhase("form");
    setSplitMode(null); setPickedItems([]); setNeedsApproval(false); setCashAlertId(null); pendingReceiptRef.current = null;
  }, [resetSignal]);

  const addToCart = (dish, note = "") => {
    setAddedFlash(dish.id);
    setTimeout(() => setAddedFlash(null), 800);
    setCart((prev) => {
      const idx = prev.findIndex((p) => p.dish_id === dish.id && p.note === note);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, { dish_id: dish.id, name: dish.name, name_en: dish.name_en, price: dish.price, qty: 1, note }];
    });
    setAddedFlash(dish.id);
    setTimeout(() => setAddedFlash(null), 800);
    if (navigator.vibrate) navigator.vibrate(15);
  };

  const changeQty = (idx, delta) => {
    setCart((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], qty: copy[idx].qty + delta };
      return copy.filter((c) => c.qty > 0);
    });
  };

  const confirmOrder = async () => {
    setConfirming(true);
    await new Promise((r) => setTimeout(r, 600));
    const res = await post("/orders", {
      table,
      items: cart.map((c) => ({ dish_id: c.dish_id, qty: c.qty, note: c.note })),
      note: "",
      by_name: myName,
    });
    setState(res.state);
    onClienteEvent("nuevo_pedido", res.order);
    setCart([]);
    setCartOpen(false);
    setConfirming(false);
    if (res.needs_approval) {
      setNeedsApproval(true);
      setScreen("tracking");
    } else if (state?.config?.service_mode === "pagar_al_instante") {
      setPayMode("instant");
      setScreen("pay");
    } else {
      setScreen("tracking");
    }
  };

  const callWaiter = async () => {
    const res = await post("/mozo", { table });
    setState(res.state);
    onClienteEvent("mozo", null);
    showToast(t.waiter_called);
  };

  const finishPayment = async (m) => {
    setPayPhase("processing");
    await new Promise((r) => setTimeout(r, 1600));
    const payload = {
      table,
      method: m,
      tip: tipValue(),
      amount: splitMode ? payBase : undefined,
      item_indexes: splitMode === "items" ? pickedItems : undefined,
    };
    if (m === "efectivo") {
      const paidItems = splitMode === "items" ? pickedItems.map((i) => allItems[i]).filter(Boolean) : allItems;
      const tip = tipValue();
      const total = grandTotal;
      const res = await post("/payments", payload);
      pendingReceiptRef.current = { id: res.alert?.id, method: "efectivo", tip, total, items: groupItems(paidItems), tip_destination: res.alert?.tip_destination || config.tip_destination };
      setCashAlertId(res.alert?.id || null);
      setCashDone(false);
      setState(res.state);
      onClienteEvent("efectivo", res.alert);
      setPayPhase("form");
      setScreen("cash");
      return;
    }
    const res = await post("/payments", payload);
    setState(res.state);
    onClienteEvent("pago", res.receipt);
    setReceipt({ ...res.receipt, items: groupItems(res.receipt.items || []) });
    setPayPhase("done");
    setTimeout(() => setScreen("receipt"), 900);
  };

  const sendReview = async () => {
    const res = await post("/reviews", { table, stars, text: reviewText, whatsapp: reviewWa });
    setState(res.state);
    onClienteEvent("resena", res.review);
    setReviewSent(true);
    setTimeout(() => setScreen("done"), 1600);
  };

  // demo: el pedido de comprobante suma un contacto al panel Impacto del dueño
  const saveContact = async (kind) => {
    const value = kind === "whatsapp" ? (reviewWa || `+54 9 11 5${String(table).padStart(3, "0")}-${String(Date.now()).slice(-4)}`) : `${(myName || "cliente").toLowerCase().replace(/\s+/g, ".")}.mesa${table}@ejemplo.com`;
    try {
      const res = await post("/contacts", { kind, value, table });
      setState(res.state);
    } catch (e) { /* solo demo */ }
  };

  const copyText = (txt, key) => {
    navigator.clipboard?.writeText(txt).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(""), 1600);
  };

  const dishName = (d) => (lang === "en" && d.name_en ? d.name_en : d.name);
  // los ítems de pedidos guardan solo el nombre en castellano: en inglés se busca el plato por id
  const itemName = (it) => (lang === "en" ? (it.name_en || dishes.find((d) => d.id === it.dish_id)?.name_en || it.name) : it.name);
  const dishDesc = (d) => (lang === "en" && d.description_en ? d.description_en : d.description);
  const dishByCat = (c) => dishes.filter((d) => d.category === c);

  const LangSwitch = ({ testid }) => (
    <div className="flex items-center gap-1 bg-[#FAFAF7] rounded-lg px-1 py-0.5 border border-[#E5E7EB]">
      {["es", "en"].map((l) => (
        <button key={l} data-testid={`${testid}-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 py-1 text-xs font-bold uppercase rounded-md transition-colors ${lang === l ? "bg-[#0A0A0A] text-[#FAFAF7]" : "text-[#0A0A0A]"}`}>
          {l}
        </button>
      ))}
    </div>
  );

  const GuideBanner = ({ text, testid }) => (
    <button data-testid={testid} onClick={onGuide}
      className="w-full bg-[#0A0A0A] text-white rounded-lg px-4 py-3 flex items-center justify-between text-sm font-bold hover:bg-neutral-800 transition">
      <span className="lowercase">{text}</span><span>{t.guide_btn}</span>
    </button>
  );

  // ---------- pantallas ----------

  const Welcome = (
    <div className="flex flex-col h-full">
      <div className="relative h-[46%] shrink-0">
        <img src={IMG_HERO} alt="Bodegón Demo" className="w-full h-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute top-4 right-4"><LangSwitch testid="lang" /></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <motion.h1 initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.08 }}
            className="text-4xl font-extrabold text-white lowercase leading-tight" data-testid="welcome-title">
            {t.welcome_title(table)}
          </motion.h1>
          <motion.p initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.14 }}
            className="text-sm text-white/85 lowercase mt-1">
            {t.welcome_sub}
          </motion.p>
        </div>
      </div>
      <div className="flex-1 bg-[#FAFAF7] rounded-t-2xl -mt-4 relative p-6 flex flex-col">
        <div className="flex items-center gap-3">
          <Logo size={44} />
          <div>
            <p className="text-xl font-extrabold lowercase">bodegón demo</p>
            <p className="text-xs text-[#525252]">{t.tagline}</p>
          </div>
        </div>
        <div className="mt-4">
          <input data-testid="my-name" placeholder={t.name_placeholder} value={myName}
            onChange={(e) => { setMyName(e.target.value); localStorage.setItem("bodegon_demo_name", e.target.value); }}
            className="w-full text-xs border border-[#E5E7EB] rounded-lg px-3 py-2.5 outline-none focus:border-[#0A0A0A] bg-white" />
        </div>
        <div className="mt-auto space-y-3">
          <button data-testid="ver-menu-btn" onClick={() => setScreen("menu")}
            className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold text-base py-4 rounded-lg hover:brightness-95 active:scale-[0.98] transition min-h-[44px]">
            {t.see_menu} →
          </button>
          <button data-testid="open-calc-cliente" onClick={openCalc}
            className="w-full bg-[#0A0A0A] text-white font-bold text-sm py-3.5 rounded-lg hover:bg-neutral-800 transition min-h-[44px]">
            ¿cuánto sumás con QR? →
          </button>
          <p className="text-center text-[11px] text-[#525252]">{t.welcome_tag}</p>
        </div>
      </div>
    </div>
  );

  const Menu = (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pt-4 pb-2 bg-[#FAFAF7] border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={30} />
            <div>
              <p className="font-extrabold lowercase leading-none">bodegón demo</p>
              <p className="text-[11px] font-mono2 text-neutral-500">{t.table(table)}</p>
            </div>
          </div>
          <LangSwitch testid="menu-lang" />
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="menu-tabs">
          {CATS.map((c) => (
            <button key={c} data-testid={`tab-${c}`} onClick={() => setCat(c)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition ${cat === c ? "bg-[#0A0A0A] text-[#FAFAF7]" : "bg-white border border-[#E5E7EB]"}`}>
              {t.categories[c]}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {dishByCat(cat).map((d) => (
          <div key={d.id} data-testid={`dish-${d.id}`}
            className={`bg-white border border-[#E5E7EB] rounded-lg p-3 flex gap-3 transition ${!d.available ? "opacity-45 grayscale" : ""}`}>
            <motion.img src={d.photo} alt={dishName(d)} loading="lazy"
              animate={addedFlash === d.id && !m.reduce ? { scale: [1, 1.04, 1] } : { scale: 1 }}
              transition={{ duration: 0.4 }}
              className="w-20 h-20 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-sm leading-tight">{dishName(d)}</p>
                  {lang === "en" && <p className="text-[10px] text-neutral-400 italic leading-tight">{d.name}</p>}
                </div>
                <p className="font-mono2 font-bold text-sm whitespace-nowrap">{money(d.price)}</p>
              </div>
              <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{dishDesc(d)}</p>
              <div className="flex items-center justify-between mt-2 gap-2">
                <div className="flex gap-1 flex-wrap">
                  {d.tags.map((tg) => (
                    <span key={tg} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${tagStyle(tg)}`}>{t.tag_labels[tg] || tg}</span>
                  ))}
                  {!d.available && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-neutral-200 text-neutral-600">{t.sold_out}</span>}
                  {d.available && state?.config && (state.config.dishes_enabled?.[d.id] === false || state.config.categories_enabled?.[d.category] === false) && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[#FEF9C3] text-[#a16207]" data-testid={`waiter-only-${d.id}`}>{t.ask_waiter}</span>
                  )}
                </div>
                {d.available && state?.config && state.config.dishes_enabled?.[d.id] !== false && state.config.categories_enabled?.[d.category] !== false && (
                  <div className="flex items-center gap-1">
                    <button data-testid={`note-${d.id}`} onClick={() => { setNoteFor(noteFor === d.id ? null : d.id); setNoteDraft(""); }}
                      className="text-[11px] font-semibold text-[#525252] underline underline-offset-2 min-h-[44px] px-1">+ {t.note}</button>
                    <motion.button data-testid={`add-${d.id}`} onClick={() => { addToCart(d, noteFor === d.id ? noteDraft : ""); setNoteFor(null); setNoteDraft(""); }}
                      whileTap={m.tap} whileHover={m.hover} transition={SPRING}
                      className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors min-h-[44px] ${addedFlash === d.id ? "bg-[#16A34A] text-white" : "bg-[#A0CDE8] text-[#0A0A0A]"}`}>
                      {addedFlash === d.id ? "✓ agregado" : `+ ${t.add}`}
                    </motion.button>
                  </div>
                )}
              </div>
              {noteFor === d.id && d.available && (
                <div className="mt-2">
                  <input data-testid={`note-input-${d.id}`} placeholder={t.note_placeholder} value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { addToCart(d, noteDraft); setNoteFor(null); setNoteDraft(""); } }}
                    className="w-full text-xs border border-[#E5E7EB] rounded-lg px-2 py-1.5 outline-none focus:border-[#0A0A0A]" />
                  <p className="text-[10px] text-neutral-400 mt-1">{t.note_hint}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* carrito colapsable */}
      {cart.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t-2 border-[#0A0A0A] slide-in-right">
          <button data-testid="cart-bar" onClick={() => setCartOpen(!cartOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold">
            <span className="flex items-center gap-2"><ChevronUp size={16} className={`transition-transform ${cartOpen ? "rotate-180" : ""}`} /> {t.view_order} · {t.itemCount(cartCount)}</span>
            <span className="font-mono2" data-testid="cart-bar-total"><RollingNumber value={cartTotal} format={(v) => money(v)} /></span>
          </button>
          {cartOpen && (
            <div className="px-4 pb-4 space-y-2.5 max-h-[40vh] overflow-y-auto">
              {cart.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{c.qty}× {itemName(c)}</p>
                    {c.note && <p className="text-[11px] text-neutral-500 italic">“{c.note}”</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`cart-minus-${i}`} onClick={() => changeQty(i, -1)} className="w-6 h-6 rounded-md border border-[#E5E7EB] flex items-center justify-center"><Minus size={12} /></button>
                    <button data-testid={`cart-plus-${i}`} onClick={() => changeQty(i, 1)} className="w-6 h-6 rounded-md border border-[#E5E7EB] flex items-center justify-center"><Plus size={12} /></button>
                    <span className="font-mono2 text-xs w-16 text-right">{money(c.price * c.qty)}</span>
                  </div>
                </div>
              ))}
              {!cart.some((c) => ["bebidas", "vinos"].includes(dishes.find((d) => d.id === c.dish_id)?.category)) && (
                <button data-testid="add-drink-suggestion" onClick={() => {
                  const lim = dishes.find((d) => d.id === "d15" && d.available);
                  if (lim) addToCart(lim);
                }} className="w-full text-left text-xs font-semibold bg-[#E3F0F9] rounded-lg px-3 py-2.5 hover:bg-[#d3e8f6] transition">
                  {t.drink_suggestion} <span className="underline underline-offset-2">{t.add_drink}</span>
                </button>
              )}
            </div>
          )}
          <div className="px-4 pb-4">
            <motion.button data-testid="confirm-order-btn" onClick={confirmOrder} disabled={confirming}
              whileTap={m.tap} whileHover={m.hover} transition={SPRING}
              className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg transition disabled:opacity-70 flex items-center justify-center gap-2 min-h-[44px]"
              style={{ minHeight: 44 }}>
              {confirming ? (<><Loader2 size={15} className="animate-spin" /> enviando…</>) : `${t.confirm_order} · ${money(cartTotal)}`}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );

  const Tracking = (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pt-4 pb-3 border-b border-[#E5E7EB] flex items-center gap-2">
        <Logo size={28} />
        <p className="font-extrabold lowercase">{t.tracking}</p>
        <span className="ml-auto text-[11px] font-mono2 text-neutral-500">{t.table(table)}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {latestOrder ? (
          <>
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-bold">{t.order_label} #{orderNo(latestOrder)}</p>
                <p className="text-[11px] font-mono2 text-neutral-500">{fmtTime(latestOrder.created_at)}</p>
              </div>
              <div className="flex items-center" data-testid="status-track">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center gap-1">
                      {/* el punto actual se remonta al avanzar: entra grande y rebota a su tamaño (spring 400/28) */}
                      <motion.div key={i === stepIndex ? `cur-${stepIndex}` : `dot-${s}`}
                        initial={i === stepIndex && !m.reduce ? { scale: 1.6 } : false}
                        animate={{ scale: 1 }}
                        transition={SPRING}
                        data-testid={i === stepIndex ? "step-current" : undefined}
                        className={`w-4 h-4 rounded-full border-2 ${i <= stepIndex ? "bg-[#16A34A] border-[#16A34A]" : "bg-white border-[#E5E7EB]"} ${i === stepIndex && i < 3 && m.reduce ? "pulse-dot" : ""}`} />
                      <span className={`text-[9px] font-bold whitespace-nowrap ${i <= stepIndex ? "text-[#16A34A]" : "text-neutral-400"}`}>{t.steps[i]}</span>
                    </div>
                    {i < 3 && (
                      <div className="flex-1 mx-1 mb-4 h-0.5 bg-[#E5E7EB] overflow-hidden rounded-full">
                        <motion.div
                          animate={{ scaleX: i < stepIndex ? 1 : 0 }}
                          initial={false}
                          transition={{ duration: m.reduce ? 0.1 : 0.3, ease: "easeOut" }}
                          style={{ transformOrigin: "left" }}
                          className="h-full w-full bg-[#16A34A]" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-1.5">
                {groupItems(latestOrder.items).map((it, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span>{it.qty}× {itemName(it)}{it.note ? <span className="text-neutral-500 italic"> “{it.note}”</span> : null}{it.by ? <span className="text-[10px] font-mono2 text-neutral-400" data-testid="item-by"> · {it.by}</span> : null}</span>
                    <span className="font-mono2">{money(it.price * it.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
            {trackOrders.slice(0, -1).reverse().map((o) => (
              <div key={o.id} className="bg-white border border-[#E5E7EB] rounded-lg p-3 opacity-70">
                <p className="text-xs font-bold">{t.order_label} #{orderNo(o)} · {fmtTime(o.created_at)} · {t.steps[STEPS.indexOf(o.status)]}</p>
                <p className="text-[11px] text-neutral-500 mt-1">{groupItems(o.items).map((i) => `${i.qty}× ${itemName(i)}`).join(", ")}</p>
              </div>
            ))}
            {(latestOrder?.approved === false || (needsApproval && latestOrder?.approved !== true)) && (
              <div className="bg-[#FEF9C3] border-2 border-[#EAB308] rounded-lg p-4 text-center" data-testid="waiting-approval">
                <p className="text-sm font-bold text-[#a16207]">{t.waiting_approval}</p>
              </div>
            )}
            <button data-testid="guide-to-gerencia" onClick={onGuide}
              className="w-full text-center text-xs font-semibold text-[#525252] underline underline-offset-2 hover:text-[#0A0A0A] transition">
              {t.guide_tracking} →
            </button>
          </>
        ) : (
          <p className="text-sm text-neutral-500 text-center mt-10">{t.no_orders}</p>
        )}
      </div>
      <div className="shrink-0 border-t border-[#E5E7EB] bg-white p-4 grid grid-cols-2 gap-2">
        <button data-testid="call-waiter-btn" onClick={callWaiter}
          className="flex items-center justify-center gap-2 border-2 border-[#0A0A0A] rounded-lg py-3 text-sm font-bold hover:bg-[#0A0A0A] hover:text-white transition">
          <Hand size={16} /> {t.call_waiter}
        </button>
        <button data-testid="order-more-btn" onClick={() => setScreen("menu")}
          className="flex items-center justify-center gap-2 border-2 border-[#0A0A0A] rounded-lg py-3 text-sm font-bold hover:bg-[#0A0A0A] hover:text-white transition">
          <Plus size={16} /> {t.order_more}
        </button>
        {state?.config?.service_mode === "solo_pedir" ? (
          latestOrder?.status === "entregado" && openTotal > 0 && (
            <div className="col-span-2 bg-[#FEF9C3] border border-[#EAB308] text-[#a16207] rounded-lg py-3 text-sm font-bold text-center" data-testid="waiter-collects">
              {t.waiter_collects}
            </div>
          )
        ) : latestOrder?.status === "entregado" && !cashPending && openTotal > 0 && (
          <button data-testid="ask-bill-btn" onClick={() => setScreen("bill")}
            className="col-span-2 bg-[#A0CDE8] text-[#0A0A0A] rounded-lg py-3.5 text-sm font-bold hover:brightness-95 transition">
            {t.ask_bill} · {money(openTotal)}
          </button>
        )}
        {cashPending && (
          <button data-testid="cash-status-btn" onClick={() => setScreen("cash")}
            className="col-span-2 bg-[#FEE2E2] text-[#DC2626] rounded-lg py-3.5 text-sm font-bold">
            {t.cash_waiting}
          </button>
        )}
      </div>
    </div>
  );

  const Bill = (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pt-4 pb-3 border-b border-[#E5E7EB] flex items-center gap-2">
        <button data-testid="bill-back" onClick={() => setScreen("tracking")}><ChevronLeft size={20} /></button>
        <p className="font-extrabold lowercase">{t.bill}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          {myOrders.map((o) => (
            <div key={o.id} className="mb-3 last:mb-0">
              <p className="text-[10px] font-mono2 text-neutral-400 uppercase">{t.order_label} #{orderNo(o)} · {fmtTime(o.created_at)}</p>
              {groupItems(o.items).map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span>{it.qty}× {itemName(it)}{it.by ? <span className="text-[10px] font-mono2 text-neutral-400"> · {it.by}</span> : null}</span>
                  <span className="font-mono2">{money(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
          ))}
          <div className="border-t border-dashed border-[#E5E7EB] mt-2 pt-2 flex justify-between font-mono2 font-bold">
            <span>{t.subtotal}</span><span data-testid="bill-subtotal">{money(openTotal)}</span>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <button data-testid="split-toggle" onClick={() => setSplitMode(splitMode ? null : "equal")}
            className="font-bold text-sm underline underline-offset-4">{t.split}</button>
          {splitMode && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {["equal", "items"].map((m) => (
                  <button key={m} data-testid={`split-${m}`} onClick={() => { setSplitMode(m); setPickedItems([]); }}
                    className={`text-xs font-bold py-2 rounded-lg border-2 ${splitMode === m ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB]"}`}>
                    {m === "equal" ? t.split_equal : t.split_items}
                  </button>
                ))}
              </div>
              {splitMode === "equal" && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button data-testid="people-minus" onClick={() => setPeople(Math.max(2, people - 1))} className="w-8 h-8 rounded-lg border border-[#E5E7EB] font-bold">−</button>
                    <span className="font-mono2 font-bold" data-testid="people-count">{people}</span>
                    <button data-testid="people-plus" onClick={() => setPeople(Math.min(12, people + 1))} className="w-8 h-8 rounded-lg border border-[#E5E7EB] font-bold">+</button>
                    <span className="text-xs text-neutral-500">{t.people}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-neutral-500">{t.each_pays}</p>
                    <p className="font-mono2 font-bold" data-testid="each-pays">{money(openTotal / people)}</p>
                  </div>
                </div>
              )}
              {splitMode === "items" && (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-neutral-500">{t.pick_items}</p>
                  {allItems.map((it, i) => (
                    <button key={i} data-testid={`pick-item-${i}`} onClick={() => setPickedItems((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i])}
                      className={`w-full flex justify-between items-center text-xs border-2 rounded-lg px-2.5 py-2 transition ${pickedItems.includes(i) ? "border-[#0A0A0A] bg-[#E3F0F9]" : "border-[#E5E7EB]"}`}>
                      <span className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${pickedItems.includes(i) ? "bg-[#0A0A0A] border-[#0A0A0A]" : "border-[#E5E7EB]"}`}>
                          {pickedItems.includes(i) && <Check size={10} className="text-white" />}
                        </span>
                        {it.qty}× {itemName(it)}{it.by ? <span className="text-[10px] font-mono2 text-neutral-400"> · {it.by}</span> : null}
                      </span>
                      <span className="font-mono2 font-bold">{money(it.price * it.qty)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="font-bold text-sm mb-2.5">{t.tip_q}</p>
          <div className="grid grid-cols-4 gap-2" data-testid="tip-options">
            {[{ v: "0", l: t.no_tip }, { v: "10", l: "10%" }, { v: "15", l: "15%" }, { v: "custom", l: t.other }].map((o) => (
              <button key={o.v} data-testid={`tip-${o.v}`} onClick={() => setTipMode(o.v)}
                className={`text-xs font-bold py-2 rounded-lg border-2 transition ${tipMode === o.v ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB]"}`}>
                {o.l}
              </button>
            ))}
          </div>
          {tipMode === "custom" && (
            <input data-testid="tip-custom" type="number" placeholder="$" value={customTip} onChange={(e) => setCustomTip(e.target.value)}
              className="mt-2 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm font-mono2 outline-none focus:border-[#0A0A0A]" />
          )}
        </div>
      </div>
      <div className="shrink-0 bg-white border-t border-[#E5E7EB] p-4">
        <div className="flex justify-between font-mono2 text-sm mb-2">
          <span className="text-neutral-500">{splitMode ? t.your_share : t.total} {tipValue() > 0 && `+ ${money(tipValue())} ${t.tip_word}`}</span>
          <span className="font-bold" data-testid="bill-total">{money(grandTotal)}</span>
        </div>
        <button data-testid="go-pay-btn" onClick={() => setScreen("pay")} disabled={splitMode === "items" && pickedItems.length === 0}
          className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg hover:brightness-95 active:scale-[0.98] transition disabled:opacity-40">
          {t.continue} → {money(grandTotal)}
        </button>
      </div>
    </div>
  );

  const methods = [
    { id: "mercadopago", label: t.method_labels.mercadopago, icon: <Smartphone size={20} /> },
    { id: "modo", label: t.method_labels.modo, icon: <Smartphone size={20} /> },
    { id: "tarjeta", label: t.method_labels.tarjeta, icon: <CreditCard size={20} /> },
    { id: "transferencia", label: t.method_labels.transferencia, icon: <Landmark size={20} /> },
    { id: "efectivo", label: t.method_labels.efectivo, icon: <Banknote size={20} /> },
  ];

  const cardValid = card.number.replace(/\s/g, "").length >= 15 && card.name.trim().length >= 3 && card.expiry.length === 5 && card.cvv.length >= 3;

  const Pay = (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pt-4 pb-3 border-b border-[#E5E7EB] flex items-center gap-2">
        <button data-testid="pay-back" onClick={() => { if (method) { setMethod(null); setPayPhase("form"); } else { setScreen(payMode === "instant" ? "tracking" : "bill"); setPayMode(null); } }}><ChevronLeft size={20} /></button>
        <p className="font-extrabold lowercase">{t.pay_with}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {!method ? (
          <div className="space-y-2.5" data-testid="payment-methods">
            {methods.map((m) => (
              <button key={m.id} data-testid={`pay-method-${m.id}`} onClick={() => setMethod(m.id)}
                className="w-full bg-white border-2 border-[#E5E7EB] rounded-lg p-4 flex items-center gap-3 text-left hover:border-[#0A0A0A] active:scale-[0.99] transition">
                <span className="w-10 h-10 rounded-lg bg-[#E3F0F9] flex items-center justify-center text-[#0A0A0A]">{m.icon}</span>
                <span className="font-bold text-sm">{m.label}</span>
                <span className="ml-auto text-neutral-400">→</span>
              </button>
            ))}
            <div className="flex justify-between font-mono2 font-bold text-sm px-1 pt-1">
              <span>{splitMode ? t.your_share : t.total}</span><span>{money(grandTotal)}</span>
            </div>
          </div>
        ) : method === "mercadopago" ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="mp-screen">
            <div className="bg-[#0A0A0A] text-white p-4">
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#A0CDE8]">{t.simulation}</p>
              <p className="font-extrabold text-lg">Mercado Pago</p>
            </div>
            <div className="p-5">
              {payPhase === "form" && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 rounded-full bg-[#A0CDE8] flex items-center justify-center font-extrabold">M</span>
                    <div>
                      <p className="font-bold text-sm">María Demo</p>
                      <p className="text-[11px] text-neutral-500">maria.demo@ejemplo.com</p>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500">{t.mp_charges}</p>
                  <p className="font-mono2 font-bold text-3xl my-3">{money(grandTotal)}</p>
                  <div className="space-y-2 mb-4">
                    {[{ id: "saldo", label: t.mp_balance, detail: `${money(152300)} ${t.available}`, icon: <Wallet size={16} /> },
                      { id: "tarjeta", label: t.mp_saved_card, detail: "Visa •••• 3704", icon: <CreditCard size={16} /> }].map((s) => (
                      <button key={s.id} data-testid={`mp-source-${s.id}`} onClick={() => setMpSource(s.id)}
                        className={`w-full flex items-center gap-3 border-2 rounded-lg px-3.5 py-3 text-left transition ${mpSource === s.id ? "border-[#0A0A0A] bg-[#FAFAF7]" : "border-[#E5E7EB]"}`}>
                        {s.icon}
                        <div className="flex-1">
                          <p className="text-sm font-bold">{s.label}</p>
                          <p className="text-[11px] text-neutral-500 font-mono2">{s.detail}</p>
                        </div>
                        <span className={`w-4 h-4 rounded-full border-2 ${mpSource === s.id ? "bg-[#0A0A0A] border-[#0A0A0A]" : "border-[#E5E7EB]"}`} />
                      </button>
                    ))}
                  </div>
                  <button data-testid="mp-approve" onClick={() => finishPayment("mercadopago")}
                    className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg active:scale-[0.98] transition">
                    {t.pay_now} {money(grandTotal)}
                  </button>
                </>
              )}
              {payPhase === "processing" && <div className="py-10 flex flex-col items-center gap-3"><Loader2 className="animate-spin" /><p className="text-sm font-semibold">{t.processing}</p></div>}
              {payPhase === "done" && (
                <div className="py-10 flex flex-col items-center gap-3">
                  <motion.svg width="52" height="52" viewBox="0 0 52 52" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.circle cx="26" cy="26" r="24" fill="none" stroke="#16A34A" strokeWidth="3"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4 }} />
                    <motion.path d="M16 27 L23 34 L37 19" fill="none" stroke="#16A34A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                  </motion.svg>
                  <p className="font-bold">{t.approved}</p>
                </div>
              )}
            </div>
          </div>
        ) : method === "modo" ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="modo-screen">
            <div className="bg-[#A0CDE8] text-[#0A0A0A] p-4">
              <p className="font-mono2 text-[10px] uppercase tracking-widest">{t.simulation}</p>
              <p className="font-extrabold text-lg">MODO</p>
            </div>
            <div className="p-5 text-center">
              {payPhase === "form" && (
                <>
                  <div className="mx-auto w-16 h-16 rounded-2xl bg-[#0A0A0A] flex items-center justify-center mb-3">
                    <Smartphone size={28} className="text-[#A0CDE8]" />
                  </div>
                  <p className="text-sm text-neutral-500">{t.modo_msg}</p>
                  <p className="font-mono2 font-bold text-3xl my-4">{money(grandTotal)}</p>
                  <button data-testid="modo-approve" onClick={() => finishPayment("modo")}
                    className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg active:scale-[0.98] transition">
                    {t.modo_confirm}
                  </button>
                </>
              )}
              {payPhase === "processing" && <div className="py-10 flex flex-col items-center gap-3"><Loader2 className="animate-spin" /><p className="text-sm font-semibold">{t.processing}</p></div>}
              {payPhase === "done" && (
                <div className="py-10 flex flex-col items-center gap-3">
                  <motion.svg width="52" height="52" viewBox="0 0 52 52" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.circle cx="26" cy="26" r="24" fill="none" stroke="#16A34A" strokeWidth="3"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4 }} />
                    <motion.path d="M16 27 L23 34 L37 19" fill="none" stroke="#16A34A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                  </motion.svg>
                  <p className="font-bold">{t.approved}</p>
                </div>
              )}
            </div>
          </div>
        ) : method === "tarjeta" ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="card-screen">
            {payPhase === "form" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-extrabold">{t.card_title}</p>
                  <p className="text-[10px] text-neutral-400">{t.card_test_hint}</p>
                </div>
                <input data-testid="card-number" placeholder={t.card_number} value={card.number} maxLength={19}
                  onChange={(e) => setCard({ ...card, number: e.target.value.replace(/\D/g, "").replace(/(.{4})/g, "$1 ").trim() })}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm font-mono2 outline-none focus:border-[#0A0A0A]" />
                <input data-testid="card-name" placeholder={t.card_name} value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm outline-none focus:border-[#0A0A0A]" />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="card-expiry" placeholder="MM/AA" value={card.expiry} maxLength={5}
                    onChange={(e) => setCard({ ...card, expiry: e.target.value.replace(/\D/g, "").replace(/(\d{2})(\d)/, "$1/$2") })}
                    className="border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm font-mono2 outline-none focus:border-[#0A0A0A]" />
                  <input data-testid="card-cvv" placeholder={t.card_cvv} value={card.cvv} maxLength={4} type="password"
                    onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "") })}
                    className="border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm font-mono2 outline-none focus:border-[#0A0A0A]" />
                </div>
                <select data-testid="card-cuotas" value={card.cuotas} onChange={(e) => setCard({ ...card, cuotas: e.target.value })}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm outline-none bg-white">
                  <option value="1">{t.installment_one(money(grandTotal))}</option>
                  <option value="3">{t.installment_n(3, money(grandTotal / 3))}</option>
                  <option value="6">{t.installment_n(6, money(grandTotal / 6))}</option>
                </select>
                <button data-testid="card-approve" onClick={() => finishPayment("tarjeta")} disabled={!cardValid}
                  className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg active:scale-[0.98] transition disabled:opacity-40">
                  {t.pay_now} {money(grandTotal)}
                </button>
              </div>
            )}
            {payPhase === "processing" && <div className="py-10 flex flex-col items-center gap-3"><Loader2 className="animate-spin" /><p className="text-sm font-semibold">{t.processing}</p></div>}
            {payPhase === "done" && (
                <div className="py-10 flex flex-col items-center gap-3">
                  <motion.svg width="52" height="52" viewBox="0 0 52 52" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.circle cx="26" cy="26" r="24" fill="none" stroke="#16A34A" strokeWidth="3"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4 }} />
                    <motion.path d="M16 27 L23 34 L37 19" fill="none" stroke="#16A34A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                  </motion.svg>
                  <p className="font-bold">{t.approved}</p>
                </div>
              )}
          </div>
        ) : method === "transferencia" ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4" data-testid="transfer-screen">
            <p className="font-extrabold">{t.transfer_title}</p>
            <p className="text-sm text-neutral-500">{t.transfer_msg}</p>
            {[["alias", "bodegon.demo.palermo"], [t.cbu, "0000003100098765432100"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between bg-[#FAFAF7] border border-[#E5E7EB] rounded-lg px-3 py-3">
                <div>
                  <p className="text-[10px] uppercase text-neutral-500 font-mono2">{k}</p>
                  <p className="font-mono2 font-bold text-sm">{v}</p>
                </div>
                <button data-testid={`copy-${k}`} onClick={() => copyText(v, k)}
                  className="flex items-center gap-1 text-xs font-bold bg-[#A0CDE8] text-[#0A0A0A] px-2.5 py-1.5 rounded-lg">
                  {copied === k ? <Check size={13} /> : <Copy size={13} />} {copied === k ? t.copied : t.copy}
                </button>
              </div>
            ))}
            <div className="flex justify-between font-mono2 font-bold text-sm">
              <span>{splitMode ? t.your_share : t.total}</span><span>{money(grandTotal)}</span>
            </div>
            {payPhase !== "processing" ? (
              <button data-testid="transfer-done" onClick={() => finishPayment("transferencia")}
                className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg active:scale-[0.98] transition">
                {t.already_paid}
              </button>
            ) : (
              <div className="py-6 flex flex-col items-center gap-3"><Loader2 className="animate-spin" /><p className="text-sm font-semibold">{t.processing}</p></div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 text-center" data-testid="cash-confirm">
            <Banknote size={36} className="mx-auto mb-3 text-[#0A0A0A]" />
            <p className="font-extrabold mb-1">{t.cash_title}</p>
            <p className="text-sm text-neutral-500 mb-4">{t.cash_prepare(money(grandTotal))}</p>
            <button data-testid="cash-approve" onClick={() => finishPayment("efectivo")}
              className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg active:scale-[0.98] transition">
              {t.cash_confirm}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const Cash = (
    <div className="flex flex-col h-full items-center justify-center p-8 text-center" data-testid="cash-waiting">
      {!cashDone ? (
        <>
          <div className="w-20 h-20 rounded-lg bg-[#FEE2E2] flex items-center justify-center mb-5">
            <Banknote size={36} className="text-[#DC2626]" />
          </div>
          <p className="font-extrabold text-lg leading-snug">{t.cash_msg}</p>
          <p className="font-mono2 font-bold text-2xl mt-4" data-testid="cash-amount">{money(grandTotal)}</p>
          <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
            <span className="w-2 h-2 rounded-full bg-[#EAB308] pulse-dot" /> {t.cash_waiting}
          </div>
          <div className="mt-8 w-full max-w-xs">
            <GuideBanner text={t.guide_cash} testid="guide-cash" />
          </div>
        </>
      ) : (
        <>
          <div className="w-20 h-20 rounded-lg bg-[#DCFCE7] flex items-center justify-center mb-5">
            <motion.svg width="40" height="40" viewBox="0 0 52 52" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <motion.path d="M14 27 L22 35 L38 18" fill="none" stroke="#16A34A" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4 }} />
            </motion.svg>
          </div>
          <p className="font-extrabold text-lg">{t.cash_paid}</p>
        </>
      )}
    </div>
  );

  const Receipt = (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5">
        <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-5" data-testid="receipt-card">
          <div className="text-center border-b border-dashed border-[#E5E7EB] pb-4 mb-4">
            <Logo size={40} />
            <p className="font-extrabold lowercase mt-2">bodegón demo</p>
            <p className="text-[11px] text-neutral-500">Av. Córdoba 4500 · Palermo</p>
            <p className="text-[10px] font-mono2 text-neutral-400 mt-1">{t.receipt_sim} #{receipt?.id || "demo"}</p>
          </div>
          {(receipt?.items || []).map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-0.5">
              <span>{it.qty}× {itemName(it)}</span>
              <span className="font-mono2">{money(it.price * it.qty)}</span>
            </div>
          ))}
          <div className="border-t border-dashed border-[#E5E7EB] mt-3 pt-3 space-y-1">
            <div className="flex justify-between text-sm"><span>{t.payment_method}</span><span className="font-bold">{t.method_labels[receipt?.method] || METHOD_LABELS[receipt?.method] || receipt?.method}</span></div>
            {receipt?.tip > 0 && <div className="flex justify-between text-sm" data-testid="receipt-tip"><span>{t.tip_word}</span><span className="font-mono2">{money(receipt.tip)}</span></div>}
            {receipt?.tip > 0 && receipt?.tip_destination && (
              <p className="text-[11px] text-neutral-500" data-testid="receipt-tip-dest">{t.tip_to(receipt.tip_destination)}</p>
            )}
            <div className="flex justify-between font-mono2 font-bold text-lg" data-testid="receipt-total">
              <span>{t.total}</span><span>{money(receipt?.total || 0)}</span>
            </div>
          </div>
          <p className="text-center text-xs text-neutral-500 mt-4">{t.receipt_msg}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid="send-email-btn" onClick={() => { showToast(t.toast_email); saveContact("email"); }}
            className="flex items-center justify-center gap-2 border-2 border-[#0A0A0A] rounded-lg py-3 text-xs font-bold hover:bg-[#0A0A0A] hover:text-white transition">
            <Mail size={14} /> {t.send_email}
          </button>
          <button data-testid="send-wa-btn" onClick={() => { showToast(t.toast_wa); saveContact("whatsapp"); }}
            className="flex items-center justify-center gap-2 border-2 border-[#0A0A0A] rounded-lg py-3 text-xs font-bold hover:bg-[#0A0A0A] hover:text-white transition">
            <MessageCircle size={14} /> {t.send_wa}
          </button>
        </div>
      </div>
      <div className="shrink-0 bg-white border-t border-[#E5E7EB] p-4">
        <button data-testid="go-review-btn" onClick={() => setScreen("review")}
          className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg hover:brightness-95 transition">
          {t.continue} →
        </button>
      </div>
    </div>
  );

  const Review = (
    <div className="flex flex-col h-full items-center justify-center p-6 text-center" data-testid="review-screen">
      {googleSim ? (
        <div className="w-full bg-white border border-[#E5E7EB] rounded-lg p-6 text-left">
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-neutral-400">{t.google_redirect}</p>
          <p className="font-extrabold text-lg mt-1">{t.google_reviews}</p>
          <p className="text-sm text-neutral-500 mt-1">Bodegón Demo — Palermo</p>
          <div className="flex gap-1 my-4">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={26} className="fill-[#A0CDE8] text-[#0A0A0A]" />)}</div>
          <div className="bg-[#FAFAF7] border border-[#E5E7EB] rounded-lg p-3 text-sm text-neutral-500 italic">{t.google_placeholder}</div>
          <button data-testid="google-publish" onClick={async () => { await sendReview(); setGoogleSim(false); }}
            className="mt-4 w-full bg-[#0A0A0A] text-white font-bold py-3 rounded-lg">{t.google_publish}</button>
        </div>
      ) : reviewSent ? (
        <>
          <div className="w-20 h-20 rounded-lg bg-[#DCFCE7] flex items-center justify-center mb-5"><Check size={36} className="text-[#16A34A]" /></div>
          <p className="font-extrabold text-lg">{t.thanks_review}</p>
        </>
      ) : (
        <div className="w-full">
          <p className="font-extrabold text-xl lowercase">{t.review_q}</p>
          <div className="flex justify-center gap-2 my-5 relative" data-testid="stars">
            {confetti && !m.reduce && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                {Array.from({ length: 8 }).map((_, i) => (
                  <motion.span key={i}
                    initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                    animate={{ opacity: 0, x: (i % 2 === 0 ? 1 : -1) * (30 + i * 8), y: -(20 + i * 10), scale: 0.4 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="absolute w-2 h-2 rounded-sm"
                    style={{ background: ["#A0CDE8", "#16A34A", "#EAB308", "#DC2626"][i % 4] }} />
                ))}
              </div>
            )}
            {[1, 2, 3, 4, 5].map((s) => (
              <motion.button key={s} data-testid={`star-${s}`}
                onClick={() => { setStars(s); if (s === 5) { setConfetti(true); setTimeout(() => setConfetti(false), 450); } }}
                whileTap={m.tap} whileHover={m.hover}
                animate={s <= stars && !m.reduce ? { scale: [1, 1.3, 1] } : { scale: 1 }}
                transition={{ ...SPRING, delay: s * 0.05 }}>
                <Star size={36} className={s <= stars ? "fill-[#A0CDE8] text-[#0A0A0A]" : "text-[#E5E7EB]"} />
              </motion.button>
            ))}
          </div>
          {stars >= 4 && (
            <button data-testid="google-review-btn" onClick={() => setGoogleSim(true)}
              className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg active:scale-[0.98] transition">
              {t.google_cta} →
            </button>
          )}
          {stars > 0 && stars <= 3 && (
            <div className="w-full space-y-3">
              <p className="text-sm font-bold text-neutral-600">{t.private_cta}</p>
              <textarea data-testid="private-review-text" rows={4} placeholder={t.private_placeholder} value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-lg p-3 text-sm outline-none focus:border-[#0A0A0A] bg-white" />
              <input data-testid="private-review-wa" placeholder={t.whatsapp_optional} value={reviewWa}
                onChange={(e) => setReviewWa(e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-lg px-3 py-3 text-sm outline-none focus:border-[#0A0A0A] bg-white" />
              <button data-testid="send-private-review" onClick={sendReview}
                className="w-full bg-[#0A0A0A] text-white font-bold py-3.5 rounded-lg">{t.send}</button>
            </div>
          )}
          {stars > 0 && <div className="mt-4 w-full"><GuideBanner text={t.guide_review} testid="guide-review" /></div>}
        </div>
      )}
    </div>
  );

  const Done = (
    <div className="flex flex-col h-full items-center justify-center p-8 text-center" data-testid="done-screen">
      <Logo size={56} />
      <p className="font-extrabold text-xl mt-4 lowercase">{t.done_title}</p>
      <p className="text-sm text-neutral-500 mt-2">{t.done_sub}</p>
      <button data-testid="restart-cliente" onClick={() => { setScreen("welcome"); setStars(0); setReviewText(""); setReviewWa(""); setReviewSent(false); setCashDone(false); setMethod(null); setCashAlertId(null); pendingReceiptRef.current = null; }}
        className="mt-6 bg-[#A0CDE8] text-[#0A0A0A] font-bold px-6 py-3 rounded-lg">{t.done_restart}</button>
    </div>
  );

  const screens = { welcome: Welcome, menu: Menu, tracking: Tracking, bill: Bill, pay: Pay, cash: Cash, receipt: Receipt, review: Review, done: Done };

  const PhoneContent = (
    <>
      <div className="shrink-0 h-9 bg-[#FAFAF7] flex items-center justify-between px-6 pt-1 z-10">
        <span className="text-[11px] font-mono2 font-bold">{new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
        <span className="flex items-center gap-1.5 text-[#0A0A0A]"><SignalHigh size={12} /><Wifi size={12} /><BatteryFull size={14} /></span>
      </div>
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait" custom={screenDir}>
          <motion.div key={screen} custom={screenDir}
            initial={(d) => (m.reduce ? { opacity: 0 } : { x: d >= 0 ? 28 : -28, opacity: 0 })}
            animate={{ x: 0, opacity: 1 }}
            exit={(d) => (m.reduce ? { opacity: 0 } : { x: d >= 0 ? -28 : 28, opacity: 0 })}
            transition={{ duration: m.reduce ? 0.1 : 0.25 }} className="h-full">
            {screens[screen]}
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          {toast && (
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: m.reduce ? 0.1 : 0.2 }}
              className="absolute bottom-4 left-4 right-4 bg-[#0A0A0A] text-white text-xs font-bold rounded-lg px-4 py-3 text-center z-20" data-testid="cliente-toast">
              {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );

  // mobile: pantalla completa, sin marco ni barra de estado simulada
  if (isMobile) {
    return (
      <div className="w-full h-full bg-[#FAFAF7] flex flex-col" data-testid="phone-mobile">
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait" custom={screenDir}>
            <motion.div key={screen} custom={screenDir}
              initial={(d) => (m.reduce ? { opacity: 0 } : { x: d >= 0 ? 28 : -28, opacity: 0 })}
              animate={{ x: 0, opacity: 1 }}
              exit={(d) => (m.reduce ? { opacity: 0 } : { x: d >= 0 ? -28 : 28, opacity: 0 })}
              transition={{ duration: m.reduce ? 0.1 : 0.25 }} className="h-full">
              {screens[screen]}
            </motion.div>
          </AnimatePresence>
          <AnimatePresence>
            {toast && (
              <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                transition={{ duration: m.reduce ? 0.1 : 0.2 }}
                className="absolute bottom-4 left-4 right-4 bg-[#0A0A0A] text-white text-xs font-bold rounded-lg px-4 py-3 text-center z-20" data-testid="cliente-toast">
                {toast}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="relative bg-[#0A0A0A] rounded-[36px] p-[10px] shadow-2xl w-[400px] max-w-full h-[820px] max-h-full" data-testid="phone-frame">
        <div className="absolute left-1/2 -translate-x-1/2 top-[18px] w-28 h-6 bg-[#0A0A0A] rounded-full z-20" />
        <div className="relative bg-[#FAFAF7] rounded-[26px] h-full overflow-hidden flex flex-col">
          {PhoneContent}
        </div>
      </div>
    </div>
  );
}
